import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Home, Plus, X, Check, Pencil, Lock, Unlock } from "lucide-react";
import { isWeakPin, WEAK_PIN_MESSAGE } from "@/lib/pinStrength";
import {
  clearHouseUnlock,
  isHouseUnlocked,
  setHouseUnlock,
} from "@/lib/houseLock";

const SUGGESTED = ["Mum's", "Dad's", "Grandparents"];

/**
 * HousesPanel — Grand and Extended tiers only.
 * Lets parents cluster members into "houses" (e.g. "Mum's", "Dad's",
 * "Grandparents") so split / blended families can see who is where at a glance.
 *
 * Props:
 *   tier     — current effective plan tier ("free"|"plus"|"grand"|"extended")
 *   onOpenBilling — callback to open BillingSheet upgrade flow
 *   onChange — notify parent when the house list changes (dashboard re-renders)
 */
export default function HousesPanel({ tier, onOpenBilling, onChange, viewerName }) {
  const gated = tier !== "extended" && tier !== "grand";

  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [busy, setBusy] = useState(false);
  // Per-house PIN manager sheet. `null` closed; otherwise the house doc.
  const [pinMgr, setPinMgr] = useState(null);

  const openPinManager = (house) => setPinMgr(house);
  const closePinManager = () => setPinMgr(null);

  const load = useCallback(async () => {
    if (gated) return;
    setLoading(true);
    try {
      const rows = await api.listHouses();
      setHouses(rows);
    } catch {
      // silent — first-time load after upgrade may race
    } finally {
      setLoading(false);
    }
  }, [gated]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      const h = await api.createHouse({ name: trimmed });
      setHouses((prev) => [...prev, h]);
      setNewName("");
      onChange && onChange();
      toast.success(`“${h.name}” added`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Couldn't add house");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (h) => {
    setEditingId(h.id);
    setEditName(h.name);
  };

  const saveEdit = async () => {
    const n = editName.trim();
    if (!n || !editingId) {
      setEditingId(null);
      return;
    }
    setBusy(true);
    try {
      const updated = await api.updateHouse(editingId, { name: n });
      setHouses((prev) => prev.map((h) => (h.id === editingId ? updated : h)));
      setEditingId(null);
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't rename");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (h) => {
    // Locked houses follow the "ask the house, not the one holding the
    // family PIN" flow. A deletion request is posted, the house's own
    // residents see a banner inside their house, and they choose. Any
    // adult with the family PIN can still delete an UNLOCKED house
    // directly — matches the FAQ copy.
    if (h.has_pin) {
      if (h.deletion_requested_at) {
        toast.info(`“${h.name}” already has a pending deletion request.`);
        return;
      }
      if (!window.confirm(
        `Ask the people inside “${h.name}” to delete it? They'll see a Do / Ignore banner next time they step in.`
      )) return;
      setBusy(true);
      try {
        const res = await api.requestDeleteHouse(h.id, viewerName || null);
        const updated = res?.house || { ...h, deletion_requested_at: new Date().toISOString() };
        setHouses((prev) => prev.map((x) => (x.id === h.id ? updated : x)));
        onChange && onChange();
        toast.success(`“${h.name}” — the people inside will decide.`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't send the request");
      } finally {
        setBusy(false);
      }
      return;
    }
    if (!window.confirm(`Delete “${h.name}”? Members in this house will become unassigned.`)) return;
    setBusy(true);
    try {
      await api.deleteHouse(h.id);
      setHouses((prev) => prev.filter((x) => x.id !== h.id));
      onChange && onChange();
      toast.success(`“${h.name}” removed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete");
    } finally {
      setBusy(false);
    }
  };

  if (gated) {
    return (
      <div className="pt-4 border-t border-[color:var(--line-soft)]">
        <label className="label-tiny block mb-2">Houses</label>
        <div
          className="rounded-xl border border-[color:var(--line-soft)] bg-[color:var(--bg-soft)] p-4 text-[13px] leading-snug text-[color:var(--ink-2)]"
          data-testid="houses-panel-gated"
        >
          <div className="flex items-center gap-2 text-[color:var(--ink)] font-medium mb-1">
            <Home className="w-4 h-4" strokeWidth={1.75} />
            Cluster members into houses
          </div>
          <p className="mb-3">
            Built for blended, three-generation and split families. Group members into
            <em> “Mum's”</em>, <em> “Dad's”</em>, <em> “Grandparents”</em> or your own names,
            and each person chooses what the other houses get to see of their check-ins.
            Available on <strong>Grand</strong> and <strong>Extended Family</strong>.
          </p>
          <button
            type="button"
            onClick={onOpenBilling}
            className="ff-btn ff-btn-primary"
            data-testid="houses-upgrade-btn"
          >
            Upgrade to Grand
          </button>
        </div>
      </div>
    );
  }

  const normalize = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const unusedSuggestions = SUGGESTED.filter(
    (s) => !houses.some((h) => normalize(h.name) === normalize(s))
  );

  return (
    <div className="pt-4 border-t border-[color:var(--line-soft)]" data-testid="houses-panel">
      <div className="flex items-center gap-2 mb-2">
        <label className="label-tiny">Houses</label>
        <span className="text-[11px] text-[color:var(--ink-3)]">{houses.length}/12</span>
      </div>

      {/* Existing houses */}
      {loading && houses.length === 0 ? (
        <div className="text-[13px] text-[color:var(--ink-3)]">Loading…</div>
      ) : null}

      {houses.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="houses-list">
          {houses.map((h) => (
            <div
              key={h.id}
              className="inline-flex items-center gap-1 rounded-full border border-[color:var(--line-soft)] bg-white pl-3 pr-1 py-1"
              data-testid={`house-chip-${h.id}`}
            >
              {editingId === h.id ? (
                <>
                  <input
                    autoFocus
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveEdit();
                      if (e.key === "Escape") setEditingId(null);
                    }}
                    maxLength={40}
                    className="ff-input !py-1 !text-[13px] !max-w-[140px]"
                    data-testid={`house-edit-input-${h.id}`}
                  />
                  <button
                    type="button"
                    onClick={saveEdit}
                    disabled={busy}
                    className="p-1.5 rounded-full hover:bg-[color:var(--bg-soft)]"
                    aria-label="Save"
                    data-testid={`house-save-${h.id}`}
                  >
                    <Check className="w-3.5 h-3.5" strokeWidth={2} />
                  </button>
                </>
              ) : (
                <>
                  <span className="text-[13px] font-medium text-[color:var(--ink)]">{h.name}</span>
                  <button
                    type="button"
                    onClick={() => startEdit(h)}
                    className="p-1.5 rounded-full hover:bg-[color:var(--bg-soft)]"
                    aria-label={`Rename ${h.name}`}
                    data-testid={`house-rename-${h.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
                  </button>
                  <button
                    type="button"
                    onClick={() => openPinManager(h)}
                    className={`p-1.5 rounded-full hover:bg-[color:var(--bg-soft)] ${h.has_pin ? "text-[color:var(--ink)]" : "text-[color:var(--ink-3)]"}`}
                    aria-label={`${h.has_pin ? "Manage" : "Set"} PIN for ${h.name}`}
                    title={h.has_pin ? "PIN set — tap to rotate/clear" : "No PIN — tap to set one"}
                    data-testid={`house-pin-btn-${h.id}`}
                  >
                    {h.has_pin ? (
                      <Lock className="w-3.5 h-3.5" strokeWidth={2} />
                    ) : (
                      <Unlock className="w-3.5 h-3.5" strokeWidth={1.75} />
                    )}
                  </button>
                  <select
                    value={h.dashboard_mode || ""}
                    onChange={async (e) => {
                      const next = e.target.value;
                      try {
                        const updated = await api.updateHouse(h.id, {
                          dashboard_mode: next,
                        });
                        setHouses((prev) =>
                          prev.map((x) => (x.id === h.id ? updated : x)),
                        );
                        onChange && onChange();
                      } catch (err) {
                        toast.error(
                          err?.response?.data?.detail || "Couldn't change mode",
                        );
                      }
                    }}
                    className="text-[12px] rounded-full border border-[color:var(--line-soft)] bg-white px-2 py-0.5"
                    aria-label={`${h.name} layout`}
                    title={`Layout inside ${h.name}`}
                    data-testid={`house-mode-${h.id}`}
                  >
                    <option value="">Family default</option>
                    <option value="full">Mood</option>
                    <option value="calendar">Calendar</option>
                    <option value="pets">Pets</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => remove(h)}
                    disabled={busy}
                    className={`p-1.5 rounded-full hover:bg-[color:var(--bg-soft)] ${h.deletion_requested_at ? "text-[color:var(--ink-2)]" : "text-[color:var(--ink-3)]"}`}
                    aria-label={h.has_pin ? `Ask to delete ${h.name}` : `Delete ${h.name}`}
                    title={
                      h.deletion_requested_at
                        ? "Already waiting on the house's answer"
                        : h.has_pin
                          ? "Ask the people inside this house to delete it"
                          : "Delete this house"
                    }
                    data-testid={`house-delete-${h.id}`}
                  >
                    <X className="w-3.5 h-3.5" strokeWidth={2} />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add new */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") add(newName);
          }}
          placeholder="Add a house (e.g. Mum's)"
          maxLength={40}
          className="ff-input !max-w-[240px]"
          data-testid="houses-new-input"
        />
        <button
          type="button"
          onClick={() => add(newName)}
          disabled={busy || !newName.trim()}
          className="ff-btn ff-btn-primary inline-flex items-center gap-1"
          data-testid="houses-add-btn"
        >
          <Plus className="w-4 h-4" strokeWidth={2} /> Add
        </button>
      </div>

      {unusedSuggestions.length > 0 && (
        <div className="mt-3">
          <div className="label-tiny mb-2">Suggestions</div>
          <div className="flex flex-wrap gap-2">
            {unusedSuggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => add(s)}
                disabled={busy}
                className="px-3 py-1 rounded-full border border-dashed border-[color:var(--line-soft)] text-[13px] text-[color:var(--ink-2)] hover:bg-[color:var(--bg-soft)]"
                data-testid={`house-suggest-${s.replace(/\W+/g, "-").toLowerCase()}`}
              >
                + {s}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3 max-w-lg text-[13px] leading-snug text-[color:var(--ink-3)]">
        Assign each family member to a house from their profile. Each person can choose
        what the other houses see of their mood, sleep and note — handy for blended
        families, exes and new partners.
      </div>

      {/* Layer 1 honesty — Houses is a privacy convenience, not a
          security boundary. This panel makes the tradeoff visible where
          the feature actually lives. Matches the /terms section and the
          FAQ entry; keep them in sync. */}
      <details
        className="mt-4 rounded-xl border border-[color:var(--line-soft)] bg-[color:var(--bg-soft)] p-3 text-[13px] leading-snug text-[color:var(--ink-2)]"
        data-testid="houses-limits-disclosure"
      >
        <summary className="cursor-pointer font-medium text-[color:var(--ink)]">
          Know the limits of Houses
        </summary>
        <div className="mt-2 space-y-2">
          <p>
            Houses is designed for <strong>amicable</strong> blended, co-parenting and
            three-generation households. It groups members under labels ("Mum's",
            "Dad's", "Grandparents"), hides a member's check-ins (mood, sleep,
            notes) from people in other houses, and — if the house has its own
            <strong> House PIN</strong> set — requires that PIN on this device
            before anyone can see or change its members' check-ins, events or
            profiles.
          </p>
          <p>
            Anyone with just the <strong>family PIN</strong> can still:
          </p>
          <ul className="list-disc pl-5 space-y-0.5">
            <li>see who lives in each house (names + avatars)</li>
            <li>see the family-wide calendar and the members list</li>
            <li>create new members (but not into a locked house without its PIN)</li>
            <li>rename or <em>request</em> deletion of a locked house — the house's own residents see a banner and decide. Unlocked houses can be renamed or deleted directly.</li>
            <li>delete <em>unassigned</em> members (anyone with no house) — so be mindful who you hand the family code to</li>
          </ul>
          <p>
            Deleting someone in a <strong>locked house</strong> needs that house's PIN,
            so no one can silently remove people behind a closed door. But unassigned
            members — and everyone in an unlocked house — are freely removable by any
            adult with the family PIN. If a former partner still has the family code
            and you share the dashboard, they can in theory delete members you've moved
            out of a house. That's the same trust model as a fridge with a key: everyone
            in the kitchen can use it.
          </p>
          <p>
            Moving an adult or teen between houses requires <strong>their own
            personal PIN</strong> (self-consent). Small kids and pets can still
            be moved by parents without a member PIN — that part is trust-based.
            Either way, you can't move someone into or out of a house whose
            <strong> House PIN</strong> you don't know.
          </p>
          <p>
            FamFeel is not suitable for <strong>adversarial</strong> co-parenting. If
            you're worried someone is misusing the family PIN, rotate it in Settings →
            Family.
          </p>
        </div>
      </details>
      {pinMgr && (
        <HousePinManager
          house={pinMgr}
          onClose={closePinManager}
          onChanged={async () => {
            await load();
            onChange && onChange();
          }}
        />
      )}
    </div>
  );
}

/**
 * HousePinManager — small sheet for setting, rotating or clearing a
 * house's PIN. When the house already has a PIN, "current PIN" is
 * required. Clears are allowed too (enter a blank new PIN).
 */
function HousePinManager({ house, onClose, onChanged }) {
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(house.has_pin ? "rotate" : "set"); // "set" | "rotate" | "clear"

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (house.has_pin && !currentPin.trim()) {
      toast.error("Enter the current house PIN");
      return;
    }
    if (mode === "clear") {
      setBusy(true);
      try {
        await api.setHousePin(house.id, {
          current_pin: currentPin.trim(),
          new_pin: "",
        });
        clearHouseUnlock(house.id);
        toast.success(`${house.name} PIN cleared`);
        await onChanged();
        onClose();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not clear PIN");
      } finally {
        setBusy(false);
      }
      return;
    }
    if (!newPin.trim() || newPin.trim().length < 6 || !/^\d+$/.test(newPin.trim())) {
      toast.error("New house PIN must be at least 6 digits");
      return;
    }
    if (isWeakPin(newPin.trim())) {
      toast.error(WEAK_PIN_MESSAGE);
      return;
    }
    if (newPin.trim() !== confirmPin.trim()) {
      toast.error("PINs don't match");
      return;
    }
    setBusy(true);
    try {
      await api.setHousePin(house.id, {
        current_pin: currentPin.trim() || undefined,
        new_pin: newPin.trim(),
      });
      // Immediately unlock on this device so the same parent doesn't hit
      // a PIN prompt 2 seconds later when they tap a member.
      setHouseUnlock(house.id, newPin.trim());
      toast.success(`${house.name} ${mode === "rotate" ? "PIN updated" : "locked"}`);
      await onChanged();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save PIN");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[color:var(--ink)]/45 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
      data-testid="house-pin-manager"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-[color:var(--bg)] md:rounded-[18px] rounded-t-[24px] w-full md:max-w-md p-5 md:p-6 space-y-4"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[color:var(--bg-soft)] flex items-center justify-center flex-shrink-0">
            <Lock className="w-4 h-4" strokeWidth={2} />
          </div>
          <div className="flex-1">
            <div className="label-tiny">House PIN</div>
            <h2 className="font-display-italic text-2xl leading-tight mt-0.5">
              {house.name}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-[color:var(--bg-soft)] flex items-center justify-center"
            aria-label="Close"
            data-testid="house-pin-close"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>
        <p className="text-[14px] leading-snug text-[color:var(--ink-2)]">
          The House PIN is the front door to this house. With it set, anyone
          wanting to see or change members, check-ins or events in {house.name}
          on this device must unlock it first.
        </p>
        {house.has_pin && (
          <div className="flex gap-2 text-[13px]">
            <button
              type="button"
              onClick={() => setMode("rotate")}
              className={`ff-chip ${mode === "rotate" ? "is-active" : ""}`}
              data-testid="house-pin-mode-rotate"
            >
              Change PIN
            </button>
            <button
              type="button"
              onClick={() => setMode("clear")}
              className={`ff-chip ${mode === "clear" ? "is-active" : ""}`}
              data-testid="house-pin-mode-clear"
            >
              Remove PIN
            </button>
          </div>
        )}
        {house.has_pin && (
          <div>
            <label className="label-tiny block mb-1">Current house PIN</label>
            <input
              type="password"
              inputMode="numeric"
              autoComplete="off"
              value={currentPin}
              onChange={(e) =>
                setCurrentPin(e.target.value.replace(/\D/g, "").slice(0, 12))
              }
              className="ff-input text-center text-xl tabular"
              data-testid="house-pin-current"
            />
          </div>
        )}
        {mode !== "clear" && (
          <>
            <div>
              <label className="label-tiny block mb-1">
                {house.has_pin ? "New house PIN" : "Set a house PIN"}
              </label>
              <input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                value={newPin}
                onChange={(e) =>
                  setNewPin(e.target.value.replace(/\D/g, "").slice(0, 12))
                }
                className="ff-input text-center text-xl tabular"
                placeholder="at least 6 digits"
                data-testid="house-pin-new"
              />
            </div>
            <div>
              <label className="label-tiny block mb-1">Confirm</label>
              <input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                value={confirmPin}
                onChange={(e) =>
                  setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 12))
                }
                className="ff-input text-center text-xl tabular"
                data-testid="house-pin-confirm"
              />
            </div>
          </>
        )}
        {mode === "clear" && (
          <p className="text-[13px] leading-snug text-[color:var(--ink-2)] rounded-xl bg-[color:var(--bg-soft)] p-3">
            Removing the PIN makes this house unlocked for everyone in the
            family workspace again. Their check-in privacy flags still apply,
            but the front-door gate is off.
          </p>
        )}
        <div className="flex items-center justify-between gap-2 pt-1">
          <div className="text-[12px] text-[color:var(--ink-3)]">
            {isHouseUnlocked(house.id) ? "Unlocked on this device" : "Locked on this device"}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="ff-btn"
              data-testid="house-pin-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="ff-btn ff-btn-primary"
              data-testid="house-pin-submit"
            >
              {busy
                ? "Saving…"
                : mode === "clear"
                  ? "Remove PIN"
                  : house.has_pin
                    ? "Save new PIN"
                    : "Set PIN"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
